import React, { useEffect, useMemo } from "react";
import { ConnectedProps, connect } from "react-redux";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ReducerState } from "../../types";
import { getOrganization } from "../../actions";
import { LatLngTuple } from "leaflet";
import { useWebServer } from "../../providers";
import { Spin } from "antd";
import "leaflet.fullscreen/dist/Control.FullScreen.css";
import "./index.css";
import MarkerManager from "./MarkerManager";
import MapExpand from "./MapExpand";
import MapDownloadControl from "./MapDownload";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { MarkerData } from "../../types/components/map";
import { TrapSite } from "../../types/api/models/trapSite";
import {
  useMaintenanceAlerts,
  useDetectionsAlerts,
} from "../../hooks/useAppAlerts";

const mapStateToProps = (state: ReducerState) => {
  const map_center = state.devices?.selected?.gps ||
    state.organizations?.map_center || { lat: 32.7361613, lon: -98.2829359 };
  return {
    uuid: state.organizations?.uuid || null,
    map_center: [map_center?.lat, map_center?.lon] as LatLngTuple,
  };
};

const connector = connect(mapStateToProps, {
  getOrganization,
});

type Props = ConnectedProps<typeof connector> & {
  /** When provided, only the marker for this trapsite is shown (mesh/trapsite routes). */
  trapSite?: Partial<TrapSite>;
};

const MapComponent: React.FC<Props> = ({
  map_center,
  uuid,
  getOrganization,
  trapSite,
}) => {
  const { sendRequest, isLoading } = useWebServer();

  useEffect(() => {
    if (!isLoading && !uuid) {
      getOrganization(sendRequest);
    }
  }, [getOrganization, sendRequest, isLoading, uuid]);

  const { data: maintenanceAlerts } = useMaintenanceAlerts();
  const { data: detectionAlerts } = useDetectionsAlerts();

  const { data: trapSitesData, isLoading: isTrapSiteLoading } =
    useListTrapSitesQuery();
  const { data: devices } = useListDevicesQuery();

  const markersData = useMemo((): MarkerData[] => {
    if (isTrapSiteLoading || !devices?.items || !trapSitesData?.items)
      return [];

    // sum maintainAlert and DetectionAlert of each device
    const deviceAgg = devices.items.map((device) => {
      const { uuid, display_name, trap_site_id, gps } = device;

      const hasMaintenanceAlert =
        maintenanceAlerts?.items?.filter((a) => a.device === uuid).length ?? 0;
      const hasDetectionAlert =
        detectionAlerts?.items?.filter((a) => a.device === uuid).length ?? 0;

      return {
        uuid: uuid || "",
        deviceName: display_name || "N/A",
        maintainCount: hasMaintenanceAlert,
        detectionCount: hasDetectionAlert,
        trapSite: trap_site_id || "",
        gps: gps,
      };
    });

    return trapSitesData.items.map((trapSite) => {
      const devicesInTrapSite = deviceAgg.filter(
        (device) => device.trapSite === trapSite._id,
      );
      const detectionCount = devicesInTrapSite.reduce(
        (sum, d) => sum + (d.detectionCount || 0),
        0,
      );
      const maintainCount = devicesInTrapSite.reduce(
        (sum, d) => sum + (d.maintainCount || 0),
        0,
      );
      const trapCount = devicesInTrapSite.length;

      return {
        id: trapSite._id,
        displayName: trapSite.display_name,
        position: [
          trapSite.gps?.lat || 0,
          trapSite.gps?.lon || 0,
        ] as LatLngTuple,
        warning: maintainCount > 0 || detectionCount > 0,
        detectionsCount: detectionCount,
        maintainCount: maintainCount,
        trapCount: trapCount,
        devicesInTrapSite: devicesInTrapSite,
      };
    });
  }, [
    devices,
    trapSitesData,
    maintenanceAlerts,
    detectionAlerts,
    isTrapSiteLoading,
  ]);

  // If trapSite has GPS, use it as the map center; otherwise fall back to Redux map_center.
  const effectiveCenter = useMemo((): LatLngTuple => {
    if (trapSite?.gps?.lat && trapSite?.gps?.lon) {
      return [trapSite.gps.lat, trapSite.gps.lon];
    }
    return map_center;
  }, [trapSite, map_center]);

  // When a specific trapSite is provided (mesh/specimen routes), show device-level markers.
  const visibleMarkers = useMemo((): MarkerData[] => {
    if (!trapSite) return markersData;

    const match = markersData.find((m) => m.id === trapSite._id);
    if (!match?.devicesInTrapSite?.length) {
      return match ? [match] : [];
    }

    // Split devices: those WITH GPS get individual markers,
    // those WITHOUT GPS are aggregated into one trapSite-level marker.
    const withGps = match.devicesInTrapSite.filter(
      (d) => d.gps?.lat && d.gps?.lon,
    );
    const withoutGps = match.devicesInTrapSite.filter(
      (d) => !d.gps?.lat || !d.gps?.lon,
    );

    const deviceMarkers: MarkerData[] = withGps.map((device) => ({
      displayName: device.deviceName,
      position: [device.gps!.lat, device.gps!.lon] as LatLngTuple,
      warning:
        (device.maintainCount || 0) > 0 || (device.detectionCount || 0) > 0,
      detectionsCount: device.detectionCount || 0,
      maintainCount: device.maintainCount || 0,
      trapCount: 1,
      markerType: "device" as const,
      gps: device.gps,
    }));

    // Aggregate GPS-less devices into one marker at the trapSite position
    if (withoutGps.length > 0) {
      deviceMarkers.push({
        displayName: match.displayName,
        position: match.position,
        warning: withoutGps.some(
          (d) => (d.maintainCount || 0) > 0 || (d.detectionCount || 0) > 0,
        ),
        detectionsCount: withoutGps.reduce(
          (s, d) => s + (d.detectionCount || 0),
          0,
        ),
        maintainCount: withoutGps.reduce(
          (s, d) => s + (d.maintainCount || 0),
          0,
        ),
        trapCount: withoutGps.length,
      });
    }

    return deviceMarkers;
  }, [markersData, trapSite]);

  return (
    <div className="map-container-big">
      {uuid && !isLoading ? (
        <MapContainer
          worldCopyJump={true}
          center={effectiveCenter}
          minZoom={3}
          maxZoom={18}
          zoom={13}
          scrollWheelZoom={true}
          style={{
            width: "100%",
            height: "100%",
          }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">Google Map</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MarkerManager
            map_center={effectiveCenter}
            markersData={visibleMarkers}
          />
          <MapExpand />
          <MapDownloadControl />
        </MapContainer>
      ) : (
        <Spin size="large" />
      )}
    </div>
  );
};

export default connector(MapComponent);
